import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Form,
  Table,
  Button,
  InputGroup,
} from "react-bootstrap";
import ReportsService from "../../../../services/reports-service";
import { IdentityContext } from "../../../../context/identity-context";
import SecurityInfoTable from "./security-info-table";
import SecurityInfoModal from "./report-submission/components/document-submission-form/components/modals/security-info-modal/security-info-modal";
import WarningModal from "./report-submission/components/document-submission-form/components/modals/warning-modal/warning-modal";

const WorkflowReportModule = () => {
  const [nameFilter, setNameFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [reportsData, setReportsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showWarning, setShowWarning] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");

  // State for modal to add security info
  const [showModal, setShowModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);

  // State to track which rows are expanded
  const [expandedRows, setExpandedRows] = useState({});

  const { user } = useContext(IdentityContext);
  const navigate = useNavigate();

  const reportsService = new ReportsService();

  // Fetch securities data based on email
  useEffect(() => {
    let isMounted = true;
    const fetchSecurities = async () => {
      setLoading(true);
      setError(null);
      try {
        const email = user?.attributes?.["email"];
        const response = await reportsService.sellingAgentListSecurities(email);
        if (isMounted) {
          setReportsData(response);
        }
      } catch (err) {
        if (isMounted) {
          setError("Failed to load securities.");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    fetchSecurities();
    return () => {
      isMounted = false;
    };
  }, [user]);

  // Build status options from nested securityInfoList entries
  const statusOptions = [
    ...new Set(
      reportsData.flatMap((report) =>
        report.securityInfoList ? report.securityInfoList.map((info) => info.status) : []
      )
    ),
  ];

  // Filter the reports. For status, we check if any nested security info matches.
  const filteredReports = reportsData.filter((report) => {
    const matchesName = nameFilter ? report.securityName.includes(nameFilter) : true;
    const matchesStatus = statusFilter
      ? report.securityInfoList &&
        report.securityInfoList.some((info) => info.status === statusFilter)
      : true;
    const matchesSearch = searchQuery
      ? report.securityName.toLowerCase().includes(searchQuery.toLowerCase())
      : true;
    return matchesName && matchesStatus && matchesSearch;
  });

  const handleEnterSecurityInfo = (report, e) => {
    e && e.stopPropagation();
    setSelectedReport(report);
    setShowModal(true);
  };

  const handleConfirmSecurityInfo = (formData) => {
    if (!selectedReport) return;
  
    // Validate BPID length
    if (formData.bpId.length !== 11) {
      setWarningMessage("BPID must be exactly 11 characters.");
      setShowWarning(true);
      return;
    }
  
    const newSecurityInfo = {
      applicationId: selectedReport.applicationId,
      clientName: formData.clientName,
      bpId: formData.bpId,
      isin: selectedReport.isin,
      esipReferenceNumber: formData.esipReferenceNumber,
      documents: formData.documents || {
        pdf1: null,
        pdf2: null,
        otherDocuments: [],
      },
      amount: formData.amount,
      rejectionHistory: formData.rejectionHistory || [],
      sellingAgentEmail: user.attributes["email"],
      interestPaymentDate: selectedReport.interestPaymentDate,
      status: formData.status || "Not Submitted",
    };
  
    const existingList = selectedReport.securityInfoList || [];
  
    // Check for duplicate BPID
    const isDuplicate = existingList.some((entry) => entry.bpId === newSecurityInfo.bpId);
    if (isDuplicate) {
      setWarningMessage(`BPID ${newSecurityInfo.bpId} already exists.`);
      setShowWarning(true);
      return;
    }
  
    // Call API and reload the page on success
    reportsService
      .patchTaxExemptApplication(
        selectedReport.applicationId,
        selectedReport.isin,
        selectedReport.interestPaymentDate,
        newSecurityInfo,
        false
      )
      .then((response) => {
        window.location.reload(); // Reload the page after success
      })
      .catch((error) => {
        console.error("Patch error:", error);
        const errorMessage =
          error.response?.data?.error ||
          "Failed to update security info due to an unknown error.";
        setWarningMessage(errorMessage);
        setShowWarning(true);
      });
  
    setShowModal(false);
    setSelectedReport(null);
  };  

  const toggleRow = (report, e) => {
    e && e.stopPropagation();
    const key = `${report.applicationId}-${report.isin}`;
    setExpandedRows((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <Container fluid>
      {/* Filters and search */}
      <Row className="mt-3">
        <Col md={3}>
          <Form.Group controlId="filterName">
            <Form.Control
              as="select"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
            >
              <option value="">Name</option>
              {[...new Set(reportsData.map((report) => report.securityName))].map(
                (name, index) => (
                  <option key={`name-${index}`} value={name}>
                    {name}
                  </option>
                )
              )}
            </Form.Control>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group controlId="filterStatus">
            <Form.Control
              as="select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">Status</option>
              {statusOptions.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </Form.Control>
          </Form.Group>
        </Col>
        <Col md={{ span: 3, offset: 3 }}>
          <InputGroup>
            <Form.Control
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ height: "auto" }}
            />
            <Button variant="primary">Search</Button>
          </InputGroup>
        </Col>
      </Row>

      {/* Reports table */}
      <Container className="report-container mb-3">
        <Row>
          <Col>
            <div style={{ maxHeight: "400px", overflowY: "auto" }}>
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th className="col-4">ISIN</th>
                    <th className="col-4">Name</th>
                    <th className="col-4">Next Interest Payment Date</th>
                    <th className="col-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReports.map((report) => {
                    const key = `${report.applicationId}-${report.isin}`;
                    return (
                      <React.Fragment key={key}>
                        {/* Main row */}
                        <tr
                          onClick={
                            report.securityInfoList && report.securityInfoList.length > 0
                              ? (e) => toggleRow(report, e)
                              : undefined
                          }
                          style={{
                            cursor:
                              report.securityInfoList && report.securityInfoList.length > 0
                                ? "pointer"
                                : "default",
                          }}
                        >
                          <td>{report.securityId}</td>
                          <td>{report.securityName}</td>
                          <td>{report.interestPaymentDate}</td>
                          <td>
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={(e) => handleEnterSecurityInfo(report, e)}
                            >
                              Add Security Info
                            </Button>
                          </td>
                        </tr>
                        {/* Nested table rendered via separate component */}
                        {report.securityInfoList &&
                          report.securityInfoList.length > 0 &&
                          expandedRows[key] && (
                            <SecurityInfoTable
                              report={report}
                              nameFilter={nameFilter}
                              statusFilter={statusFilter}
                              searchQuery={searchQuery}
                              onUpdateSecurityInfoList={(updatedList) => {
                                setReportsData((prevReports) =>
                                  prevReports.map((r) => {
                                    if (
                                      r.applicationId === report.applicationId &&
                                      r.isin === report.isin
                                    ) {
                                      return { ...r, securityInfoList: updatedList };
                                    }
                                    return r;
                                  })
                                );
                              }}
                            />
                          )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          </Col>
        </Row>
      </Container>

      {/* Security Info Modal */}
      <SecurityInfoModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onConfirm={handleConfirmSecurityInfo}
      />

      <WarningModal
        show={showWarning}
        onHide={() => setShowWarning(false)}
        message={warningMessage}
      />
    </Container>
  );
};

export default WorkflowReportModule;
